import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { money } from '../lib/format';
import type { DriverRow, ZoneRow } from '../lib/types';
import { Undo2, X } from 'lucide-react';
import { InlineIcon } from '../components/InlineIcon';

export function Drivers({
  drivers,
  zones,
  permissions,
  onChanged,
}: {
  drivers: DriverRow[];
  zones: ZoneRow[];
  permissions: string[];
  onChanged: () => Promise<void>;
}) {
  /** 改的是司机挣多少 —— 和「能建账号」不是一个权限 */
  const canPrice = permissions.includes('pricing:manage');
  const [payoutFor, setPayoutFor] = useState<DriverRow | null>(null);
  const [creating, setCreating] = useState(false);
  /** 初始密码只在创建/重置时返回一次,之后无法再查看 */
  const [credential, setCredential] = useState<{ name: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function resetPassword(d: DriverRow) {
    if (!confirm(`重置 ${d.name} 的密码?这会让他当前的登录立即失效。`)) return;
    try {
      const r = await api<{ name: string; initialPassword: string }>(
        `/drivers/${d.id}/reset-password`,
        { method: 'POST' },
      );
      setCredential({ name: r.name, password: r.initialPassword });
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '重置失败');
    }
  }

  async function toggleStatus(d: DriverRow) {
    const next = d.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      await api(`/drivers/${d.id}`, { method: 'PATCH', body: { status: next } });
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '操作失败');
    }
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold lg:text-2xl text-slate-900 dark:text-slate-100">
          司机
        </h1>
        <button
          onClick={() => setCreating(true)}
          className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium"
        >
          + 新建司机
        </button>
      </div>

      {error && (
        <div
          className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-4 py-2 text-red-700 dark:text-red-300 text-sm"
        >
          {error}
        </div>
      )}

      <div
        className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto
                      bg-white dark:bg-slate-800"
      >
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
            <tr>
              <Th>工号</Th>
              <Th>姓名</Th>
              <Th>手机</Th>
              <Th>类型</Th>
              <Th>车辆</Th>
              <Th>状态</Th>
              <Th>负载</Th>
              <Th>今日</Th>
              <Th>超时</Th>
              <Th>操作</Th>
            </tr>
          </thead>
          <tbody>
            {drivers.map((d) => (
              <tr key={d.id} className="border-t border-slate-100 dark:border-slate-700/50">
                <td className="p-3 font-mono text-xs text-slate-500">{d.driverNo}</td>
                <td className="p-3 text-slate-900 dark:text-slate-100">
                  {d.name}
                  {d.mustChangePassword && (
                    <span
                      title="尚未修改初始密码"
                      className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950
                                 text-amber-700 dark:text-amber-400"
                    >
                      未改密
                    </span>
                  )}
                </td>
                <td className="p-3 text-slate-600 dark:text-slate-400">{d.phone}</td>
                <td className="p-3 text-slate-600 dark:text-slate-400">
                  {{ EMPLOYEE: '自有', CONTRACTOR: '承包', CROWD: '众包' }[d.driverType] ??
                    d.driverType}
                </td>
                <td className="p-3 text-slate-600 dark:text-slate-400">{d.plateNo ?? '—'}</td>
                <td className="p-3">
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded ${
                      d.status !== 'ACTIVE'
                        ? 'bg-slate-100 dark:bg-slate-700 text-slate-500'
                        : d.dutyStatus === 'OFFLINE'
                          ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                          : 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-400'
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        d.status === 'ACTIVE' && d.dutyStatus !== 'OFFLINE'
                          ? 'bg-green-500'
                          : 'bg-slate-400'
                      }`}
                    />
                    {d.status !== 'ACTIVE'
                      ? '已停用'
                      : d.dutyStatus === 'OFFLINE'
                        ? '离线'
                        : '在线'}
                  </span>
                </td>
                <td className="p-3">
                  <LoadBar count={d.currentOrderCount} />
                </td>
                <td className="p-3">
                  <TodayStats today={d.today} />
                </td>
                {/*
                  ★ 近 7 天「派了没接、被系统收回」的次数。
                    调度选司机时最想知道的一件事就是「这个人接不接单」——
                    在这之前它只存在于数据库里,界面上一个字都没有,
                    于是调度只能凭印象,或者派出去之后干等。
                */}
                <td className="p-3">
                  {(d.timeout7d ?? 0) === 0 ? (
                    <span className="text-slate-300 dark:text-slate-600">—</span>
                  ) : (
                    <span
                      className={
                        (d.timeout7d ?? 0) >= 3
                          ? 'text-amber-600 dark:text-amber-400 font-medium'
                          : 'text-slate-500'
                      }
                      title="近 7 天派单后超时未接的次数"
                    >
                      {d.timeout7d} 次
                    </span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => void resetPassword(d)}
                      className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                    >
                      重置密码
                    </button>
                    <button
                      onClick={() => void toggleStatus(d)}
                      className="text-xs text-slate-500 hover:underline"
                    >
                      {d.status === 'ACTIVE' ? '停用' : '启用'}
                    </button>
                    {canPrice && (
                      <button
                        onClick={() => setPayoutFor(d)}
                        className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                      >
                        专属酬劳
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {drivers.length === 0 && (
              <tr>
                <td colSpan={8} className="p-10 text-center text-slate-400">
                  还没有司机,点右上角新建
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {payoutFor && (
        <PayoutRules driver={payoutFor} zones={zones} onClose={() => setPayoutFor(null)} />
      )}

      {creating && (
        <CreateDriver
          onClose={() => setCreating(false)}
          onCreated={async (c) => {
            setCreating(false);
            setCredential(c);
            await onChanged();
          }}
        />
      )}

      {credential && (
        <CredentialDialog credential={credential} onClose={() => setCredential(null)} />
      )}
    </div>
  );
}

interface PayoutRule {
  id: string;
  zone: { id: string; name: string } | null;
  customer: { id: string; name: string } | null;
  mode: string;
  value: number;
  note: string | null;
  effectiveFrom: string;
  isActive: boolean;
}

const MODE_LABEL: Record<string, string> = {
  BASE_OVERRIDE: '只替起步价',
  FIXED: '固定金额',
  PERCENT: '按运费抽成',
  PER_PACKAGE: '按件',
};

/**
 * 每种模式下「这一单他到手多少」怎么算 —— 摆在选择框底下。
 *
 * ★ 这四行是这一屏最该有的东西。四种模式的名字都很短,
 *   而它们算出来的钱能差一倍:「固定金额」是不管跑多远都这个数,
 *   「只替起步价」是起步价换成他的、加价照商家的配置照给。
 *   不写清楚的话,运营配完要等司机来问才发现配错了 —— 而那是钱。
 */
const MODE_HINT: Record<string, string> = {
  BASE_OVERRIDE:
    '用这个数顶掉商家在这个区的起步价,续重 / 多件 / 里程 / 偏远加价全照商家自己的配置照给。' +
    '例:商家起步价 8、续重 2、里程 5、偏远 3,这里填 12 → 他拿 22',
  FIXED: '不管跑多远、多重,这一单就这个数。加价一律不另算',
  PERCENT: '按这一单的配送费抽成(税前、不含代收手续费)',
  PER_PACKAGE: '按件数算,每件这个数',
};

/**
 * 这个司机的专属酬劳。
 *
 * ★ 这一屏只配**例外**。一条都没有 = 他按默认价拿钱,
 *   所以空列表要说成「按默认价」,而不是「暂无数据」——
 *   后者看起来像没加载出来。
 *
 * ★ 这个数在**派单时**才落到订单上。在这里改价,不会动已经派出去的单 ——
 *   那些单的价是派的时候谈的。要写出来,否则运营会以为改完全都变了。
 */
function PayoutRules({
  driver,
  zones,
  onClose,
}: {
  driver: DriverRow;
  zones: ZoneRow[];
  onClose: () => void;
}) {
  const [rows, setRows] = useState<PayoutRule[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ zoneId: '', mode: 'FIXED', value: '', note: '' });

  const load = useCallback(async () => {
    try {
      setRows(await api<PayoutRule[]>(`/drivers/${driver.id}/payout-rules`));
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, [driver.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function add() {
    const raw = form.value.trim();
    if (raw === '') return;
    setBusy(true);
    try {
      await api(`/drivers/${driver.id}/payout-rules`, {
        method: 'POST',
        body: {
          ...(form.zoneId ? { zoneId: form.zoneId } : {}),
          mode: form.mode,
          /*
           * ★ 屏幕上写人看得懂的数,库里存最小单位 ——
           *   让人把 8% 敲成 800、把 9 块敲成 900,迟早有人差 100 倍。
           *
           *   两种模式的换算**恰好都是 ×100**(百分数→万分比、元→分),
           *   所以这里只有一行。写成一行不是偷懒:分成两个分支写
           *   同样的算式,以后改其中一个的人会以为另一个不用改。
           */
          value: Math.round(Number(raw) * 100),
          ...(form.note.trim() ? { note: form.note.trim() } : {}),
        },
      });
      setForm({ zoneId: '', mode: 'FIXED', value: '', note: '' });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    try {
      await api(`/driver-payout-rules/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '删除失败');
    }
  }

  return (
    <Modal title={`${driver.name} 的专属酬劳`} onClose={onClose}>
      <p className="mb-3 text-xs text-slate-500">
        只配例外 —— 没有规则的区域按默认价算。
        {/* ★ 「什么时候生效」必须写在眼前:不写的话运营改完会去翻已派的单,
               发现没变,然后以为功能坏了 */}
        <span className="mt-0.5 block">
          改动在<span className="font-medium">下一次派单</span>时生效 ——
          已经派出去的单不受影响,那些单的价是派单当时定的
        </span>
      </p>

      {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      {rows === null ? (
        <div className="py-6 text-center text-sm text-slate-400">读取中…</div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg bg-slate-50 px-3 py-4 text-center text-sm text-slate-500
                        dark:bg-slate-900/50"
        >
          还没有专属规则 —— 他现在按默认价拿钱
        </div>
      ) : (
        <ul className="mb-3 space-y-1.5">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border
                         border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
            >
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {r.zone?.name ?? '所有区域'}
              </span>
              <span className="text-slate-500">{MODE_LABEL[r.mode] ?? r.mode}</span>
              <span className="font-medium">
                {r.mode === 'PERCENT' ? `${(r.value / 100).toFixed(2)}%` : money(r.value)}
                {/* ★ 「只替起步价」要写出后面还有加价 —— 只显示一个金额的话,
                       看的人会以为他这一单就拿这么多 */}
                {r.mode === 'BASE_OVERRIDE' && (
                  <span className="ml-1 text-xs font-normal text-slate-400">+ 各项加价</span>
                )}
              </span>
              {r.customer && <span className="text-xs text-slate-400">限 {r.customer.name}</span>}
              {r.note && <span className="text-xs text-slate-400">{r.note}</span>}
              <button
                onClick={() => void remove(r.id)}
                className="ml-auto text-xs text-red-600 hover:underline dark:text-red-400"
              >
                删除
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <div className="mb-2 text-xs font-medium text-slate-600 dark:text-slate-300">加一条</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">区域</span>
            <select
              aria-label="区域"
              value={form.zoneId}
              onChange={(e) => setForm({ ...form, zoneId: e.target.value })}
              className={SELECT}
            >
              <option value="">所有区域</option>
              {zones
                .filter((z) => z.isActive)
                .map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">怎么算</span>
            <select
              aria-label="怎么算"
              value={form.mode}
              onChange={(e) => setForm({ ...form, mode: e.target.value })}
              className={SELECT}
            >
              {Object.entries(MODE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            {MODE_HINT[form.mode] && (
              <span className="mt-1 block text-xs text-slate-500">{MODE_HINT[form.mode]}</span>
            )}
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">
              {form.mode === 'PERCENT'
                ? '抽成(%)'
                : form.mode === 'BASE_OVERRIDE'
                  ? '起步价(元)'
                  : '金额(元)'}
            </span>
            <input
              aria-label="金额"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              placeholder={form.mode === 'PERCENT' ? '例如 70' : '例如 9.00'}
              className={SELECT}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">备注(为什么给他这个价)</span>
            <input
              aria-label="备注"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="半年后没人记得这个价是怎么谈的"
              className={SELECT}
            />
          </label>
        </div>
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => void add()}
            disabled={busy || form.value.trim() === ''}
            className="rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-medium text-white
                       disabled:opacity-40"
          >
            {busy ? '保存中…' : '加上'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const SELECT =
  'w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm';

function CreateDriver({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (c: { name: string; password: string }) => Promise<void>;
}) {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    // 默认轿车 —— 这边跑车队的绝大多数是轿车,摩托是少数
    vehicleType: 'CAR',
    /*
     * ★ 初始密码给一个默认值,而不是留空让人想。
     *   司机的初始密码是当面或在微信里告知的 —— 一串随机大写字母
     *   在电话里念三遍还会念错。他第一次登录必须改掉,所以这里
     *   要的是「好念」,不是「难猜」。
     */
    password: '123456',
    plateNo: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (busy || !form.name || !form.phone) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ name: string; initialPassword: string }>('/drivers', {
        method: 'POST',
        // 留空就不传 —— 后端会随机生成一个,而不是建一个空密码的账号
        body: { ...form, password: form.password.trim() || undefined },
      });
      await onCreated({ name: r.name, password: r.initialPassword });
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '创建失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="新建司机" onClose={onClose}>
      {error && (
        <div
          className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 px-4 py-2 text-red-700
                        dark:text-red-300 text-sm"
        >
          {error}
        </div>
      )}
      <div className="space-y-3">
        <Field label="姓名" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <Field
          label="手机号"
          value={form.phone}
          onChange={(v) => setForm({ ...form, phone: v })}
          hint="本地格式即可,系统会统一存成国际格式"
        />
        <div>
          <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">车辆类型</label>
          <select
            value={form.vehicleType}
            onChange={(e) => setForm({ ...form, vehicleType: e.target.value })}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700
                       bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
          >
            <option value="CAR">轿车</option>
            <option value="VAN">面包车</option>
            <option value="TRUCK">卡车</option>
            <option value="MOTORCYCLE">摩托车</option>
          </select>
        </div>
        <Field
          label="初始密码"
          value={form.password}
          onChange={(v) => setForm({ ...form, password: v })}
          hint="默认 123456 —— 司机第一次登录会强制改掉"
        />
        <Field
          label="车牌"
          value={form.plateNo}
          onChange={(v) => setForm({ ...form, plateNo: v })}
        />
      </div>

      <div className="mt-5 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                     text-slate-700 dark:text-slate-300"
        >
          取消
        </button>
        <button
          onClick={() => void submit()}
          disabled={busy || !form.name || !form.phone}
          className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
        >
          {busy ? '创建中…' : '创建'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * 初始密码只显示这一次。
 * 关掉就再也看不到 —— 忘了只能重置。所以必须让管理员意识到要立刻记下来。
 */
function CredentialDialog({
  credential,
  onClose,
}: {
  credential: { name: string; password: string };
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal title="初始密码" onClose={onClose}>
      <p className="text-slate-600 dark:text-slate-400 mb-3">
        请把这个密码告知{' '}
        <strong className="text-slate-900 dark:text-slate-100">{credential.name}</strong>,
        他首次登录后需要自行修改。
      </p>
      <div className="flex items-center gap-2">
        <code
          className="flex-1 px-4 py-3 rounded-lg bg-slate-100 dark:bg-slate-900
                         font-mono text-xl tracking-widest text-slate-900 dark:text-slate-100"
        >
          {credential.password}
        </code>
        <button
          onClick={() => {
            void navigator.clipboard.writeText(credential.password);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600
                     text-slate-700 dark:text-slate-300 text-sm whitespace-nowrap"
        >
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">
        ⚠ 这个密码只显示这一次,关闭后无法再查看
      </p>
      <button
        onClick={onClose}
        className="mt-4 w-full h-10 rounded-lg bg-brand-600 text-white font-medium"
      >
        我已记录
      </button>
    </Modal>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      {/*
        ★ 补上 role / aria-label:这个 Modal 原来只是一个 div ——
          读屏念不出「这是个对话框」,也念不出它的标题,
          而键盘用户按 Tab 会直接走到被遮住的背景里去。
          (顺带让用例能按名字定位,不用去猜 DOM 结构。)
      */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6
                   shadow-2xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700
                   bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
      />
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

/**
 * 负载条。
 *
 * ★ 光写「11 单」调度员没法判断还能不能再派 —— 得看它离上限多远。
 *   接近上限时变色,这样扫一眼就知道该派给谁。
 *   上限暂用 policy 的默认值 30(V1 只有 EMPLOYEE 一种用工类型)。
 */
function LoadBar({ count }: { count: number }) {
  const max = 30;
  const pct = Math.min(100, (count / max) * 100);
  const heavy = count >= max * 0.8;
  const busy = count >= max * 0.5;

  return (
    <div className="min-w-24">
      <div className="text-sm tabular-nums text-slate-900 dark:text-slate-100">
        {count} <span className="text-xs text-slate-400">/ {max}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
        <div
          className={`h-full rounded-full ${
            heavy ? 'bg-red-500' : busy ? 'bg-amber-500' : 'bg-green-500'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** 今日战况。零值淡显 —— 有问题的数字要能一眼跳出来 */
function TodayStats({
  today,
}: {
  today?: { delivered: number; failed: number; returned: number };
}) {
  const t = today ?? { delivered: 0, failed: 0, returned: 0 };
  const total = t.delivered + t.failed + t.returned;

  if (total === 0) {
    return <span className="text-xs text-slate-400">今天还没跑</span>;
  }

  // 完成率:失败和退回都算没送到。只看完成数会掩盖问题司机
  const rate = Math.round((t.delivered / total) * 100);

  return (
    <div className="text-xs whitespace-nowrap">
      <span className="text-slate-900 dark:text-slate-100">✓ {t.delivered}</span>
      {t.failed > 0 && <span className="ml-2 text-red-600 dark:text-red-400"><InlineIcon icon={X} /> {t.failed}</span>}
      {t.returned > 0 && (
        <span className="ml-2 text-orange-600 dark:text-orange-400"><InlineIcon icon={Undo2} /> {t.returned}</span>
      )}
      <span className={`ml-2 ${rate < 80 ? 'text-red-600 dark:text-red-400' : 'text-slate-400'}`}>
        {rate}%
      </span>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="p-3 text-left font-medium whitespace-nowrap">{children}</th>;
}
